import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { CopyableIdRow } from 'src/components/shared/CopyableIdRow'
import type { WebhookSubscription } from 'src/interfaces/webhook'
import { WebhookSubscriptionForm } from './WebhookSubscriptionForm'
import type {
  WebhookSubscriptionFormMode,
  WebhookSubscriptionFormValues,
} from './WebhookSubscriptionForm.interface'

const FORM_ID = 'webhook-subscription-form'

interface Props {
  isOpen: boolean
  mode: WebhookSubscriptionFormMode
  subscription: WebhookSubscription | null
  isSaving: boolean
  onSubmit(values: WebhookSubscriptionFormValues): void
  onClose(): void
}

const WebhookSubscriptionFormModalComponent = ({
  isOpen,
  mode,
  subscription,
  isSaving,
  onSubmit,
  onClose,
}: Props) => {
  return (
    <CommonModal
      isOpen={isOpen}
      title={mode === 'edit' ? 'Edit webhook subscription' : 'New webhook subscription'}
      // Wider than the default confirm-dialog width: this modal carries three checkbox groups.
      className={tw`max-w-[560px]`}
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="dark"
            type="submit"
            form={FORM_ID}
            loading={isSaving}
            disabled={isSaving}
            data-testid="webhook-subscription-form-submit-button"
          >
            {mode === 'edit' ? 'Save changes' : 'Create subscription'}
          </Button>
        </Fragment>
      }
    >
      {mode === 'edit' && subscription ? (
        // Read-only (ENG-1221): the ID is never edited or sent.
        <CopyableIdRow
          title="Subscription ID"
          value={subscription.id}
          copiedMessage="Subscription ID copied"
          copyAriaLabel="Copy subscription ID"
          className={tw`mb-4`}
          dataTestId="webhook-subscription-form-id"
          copyButtonDataTestId="webhook-subscription-form-copy-id-button"
        />
      ) : null}
      <WebhookSubscriptionForm
        formId={FORM_ID}
        mode={mode}
        subscription={subscription}
        onSubmit={onSubmit}
      />
    </CommonModal>
  )
}

export const WebhookSubscriptionFormModal = memo(WebhookSubscriptionFormModalComponent)
